import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams, useSearchParams } from 'react-router-dom'
import { api, type ArticleDetail, type Region } from '../lib/api'
import { ErrorNote } from '../components/Page'
import { AccessNotice, SeatNotice } from '../components/SeatNotice'
import { ReadingView, type ReadingRecord } from '../components/viewer/ReadingView'
import { RecordMissing } from '../components/viewer/RecordMissing'
import { readingPassages } from '../components/viewer/model'
import { useSession } from '../lib/session'

/**
 * One article, readable, with a citation and the crop it was cut from.
 *
 * `/article/:id` is the older of the two reading routes and stays because
 * every permalink already minted points at it. It reads the same record the
 * unit route does, through `/api/articles/{id}`.
 */

function regionOf(article: ArticleDetail): Region | null {
  if (article.region) return article.region
  const onPage = article.blocks.filter(
    (block) => !article.page_id || !block.page_id || block.page_id === article.page_id,
  )
  if (!onPage.length) return null
  return [
    Math.min(...onPage.map((b) => b.bbox[0])),
    Math.min(...onPage.map((b) => b.bbox[1])),
    Math.max(...onPage.map((b) => b.bbox[2])),
    Math.max(...onPage.map((b) => b.bbox[3])),
  ]
}

export function ArticlePage() {
  const { articleId = '' } = useParams()
  const { config, can } = useSession()
  const [params] = useSearchParams()
  const [editing, setEditing] = useState(params.get('edit') === '1')

  const article = useQuery({
    queryKey: ['article', articleId],
    queryFn: () => api.get<ArticleDetail>(`/articles/${articleId}?words=true`),
    retry: false,
  })

  if (article.isError) {
    return (
      <div className="p-4 md:p-6">
        <SeatNotice error={article.error} onRetry={() => void article.refetch()} />
        <AccessNotice error={article.error} />
        <RecordMissing
          error={article.error}
          what="article"
          onRetry={() => void article.refetch()}
        />
      </div>
    )
  }
  if (!article.data) {
    return <p className="p-6 text-[15px] text-ink2">Opening the article.</p>
  }

  const data = article.data
  if (editing) {
    return <ArticleEditor article={data} onClose={() => setEditing(false)} />
  }

  const engine = data.blocks[0]?.source_engine ?? null
  const record: ReadingRecord = {
    kind: data.kind ?? 'article',
    title: data.headline,
    subtitle: data.deck ?? data.subhead,
    byline: data.byline,
    pubDate: data.pub_date,
    pageLabel: data.section_label ?? (data.page_number ? String(data.page_number) : null),
    pageId: data.page_id,
    region: regionOf(data),
    route: null,
    engine,
    confidence: data.confidence,
    wordCount: data.word_count,
    isWire: data.is_wire,
    wireService: data.wire_service,
    columnsLabel: null,
    publication: data.citation?.publication ?? null,
    correctionSource: data.correction_source,
    textRaw: data.raw_text,
    textCorrected: data.corrected_text,
    blocks: data.blocks,
    citation: data.citation,
    permalink: data.citation?.permalink ?? null,
    needsReview: data.needs_review,
    jumpTo: data.jump?.to ?? null,
    editionId: data.edition_id ?? null,
    pageNumber: data.page_number,
  }

  return (
    <ReadingView
      // Remounted after an edit so it opens on the text just saved.
      key={`${data.correction_source ?? 'raw'}:${data.word_count ?? 0}`}
      record={record}
      productName={config?.product_name ?? ''}
      backHref="/search"
      actions={
        can('review.item.edit') ? (
          <button type="button" className="btn-secondary" onClick={() => setEditing(true)}>
            Edit article
          </button>
        ) : null
      }
    />
  )
}

/**
 * Correcting an article. The text saved is kept beside what the paper printed,
 * never over it: the reader shows the edit and the original one click away.
 */
function ArticleEditor({ article, onClose }: { article: ArticleDetail; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [headline, setHeadline] = useState(article.headline ?? '')
  const [deck, setDeck] = useState(article.deck ?? '')
  const [byline, setByline] = useState(article.byline ?? '')
  // An unedited article opens as the paragraphs the paper printed, not as
  // the raw text with a break at every line and column.
  const [text, setText] = useState(
    () =>
      article.corrected_text ??
      (readingPassages(article.blocks, false)
        .map((passage) => passage.text)
        .join('\n\n') ||
        article.raw_text ||
        ''),
  )

  const save = useMutation({
    mutationFn: () =>
      api.patch<ArticleDetail>(`/articles/${article.id}`, { headline, deck, byline, text }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['article', article.id], updated)
      void queryClient.invalidateQueries({ queryKey: ['search'] })
      onClose()
    },
  })

  return (
    <form
      className="mx-auto w-full max-w-[860px] px-4 py-6 md:px-8 md:py-10"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate()
      }}
    >
      <div className="chrome mb-4">Edit article</div>
      <label className="label" htmlFor="edit-headline">
        Headline
      </label>
      <input
        id="edit-headline"
        className="input mb-4 w-full text-[20px]"
        value={headline}
        onChange={(event) => setHeadline(event.target.value)}
      />
      <label className="label" htmlFor="edit-deck">
        Deck
      </label>
      <input
        id="edit-deck"
        className="input mb-4 w-full"
        value={deck}
        onChange={(event) => setDeck(event.target.value)}
      />
      <label className="label" htmlFor="edit-byline">
        Byline
      </label>
      <input
        id="edit-byline"
        className="input mb-4 w-full"
        value={byline}
        onChange={(event) => setByline(event.target.value)}
      />
      <label className="label" htmlFor="edit-text">
        Text
      </label>
      <textarea
        id="edit-text"
        className="input mb-2 min-h-[420px] w-full text-[16px] leading-[1.55]"
        value={text}
        onChange={(event) => setText(event.target.value)}
      />
      <p className="ident mb-4 text-ink2">
        Saved beside the recognised text, which stays as the paper printed it. Searchable as soon
        as it is saved.
      </p>
      <ErrorNote error={save.error} />
      <div className="flex gap-2">
        <button type="submit" className="btn-primary" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  )
}
